import { anyRarity } from './content.js';
const JARL = { mob: 'jarl', biome: 'snow' };
const SKALD = { mob: 'snow-skald', biome: 'snow' };
const SEER = { mob: 'snow-seer', biome: 'snow' };
const HUNTRESS = { mob: 'snow-huntress', biome: 'snow' };
const SCOUT = { mob: 'snow-scout', biome: 'snow' };
const WARDEN = { mob: 'snow-warden', biome: 'snow' };
const SMITH = { mob: 'smith', biome: 'snow' };
const MERCHANT = { mob: 'merchant', biome: 'snow' };
const SMELTER = { mob: 'smelter', biome: 'snow' };
const ALCHEMIST = { mob: 'alchemist', biome: 'snow' };
const CAPTAIN = { mob: 'captain', biome: 'meadow' };
/**
 * The snow's chain, the last and longest of the game: from Jarl Haldor's
 * hall to the Frost Giant's throne, by the skald who sings the old saga,
 * the seer of the crystals, the huntress of the Bear's Cave, the scout of
 * the Ridge and Ulfar, the last survivor of the expedition that failed
 * twenty winters ago. Three elites (two called out by a bait, the
 * legendary hunted in the wild), two camps, the frost set bought, worn and
 * raised to +6. The Giant was the last lock of Shefira's seal (ADR 0067):
 * the lines foreshadow it (Runa's forgotten verse, Eira's visions) and the
 * farewell sounds wrong; what follows is in `snow-dragon.ts`, and the tale
 * carried home to Havrebourg waits for La Brèche.
 */
export const SNOW_QUESTS = [
    {
        id: 'snow-portal',
        name: 'L’arche des neiges',
        biome: 'snow',
        level: 16,
        giver: JARL,
        turnIn: JARL,
        offer: 'Je suis Haldor, le chef de Givrecime. Avant qu’on parle, va toucher l’arche de la place. Ici, dans une tempête de neige, on ne voit même plus le bout de son nez. L’arche, elle, te ramènera toujours au chaud.',
        summary: 'Enregistrer le portail de Givrecime, puis revenir au Jarl Haldor.',
        pending: 'L’arche est au milieu de la place. Dépêche-toi, avant que le vent se lève.',
        complete: 'Voilà ! Maintenant, tu peux revenir te réchauffer à ma halle depuis n’importe où. Et crois-moi, tu vas revenir souvent : l’hiver dure trois mois de trop, cette année.',
        objectives: [{ kind: 'portal', label: 'Enregistrer le portail', portal: 'snow' }],
        reward: { xp: 250, gold: 50 },
    },
    {
        id: 'snow-council',
        name: 'Le conseil de Givrecime',
        biome: 'snow',
        level: 16,
        requires: ['snow-portal'],
        giver: JARL,
        turnIn: JARL,
        offer: 'Ça fait trois mois que le vent souffle sans arrêt, et les bêtes descendent des montagnes. Les anciens disent que le Géant de Givre se réveille sur son trône. Bon, les anciens disent beaucoup de choses. Va d’abord voir le forgeron, le fondeur, l’alchimiste et le marchand, qu’ils apprennent ton nom.',
        summary: 'Saluer le forgeron, le fondeur, l’alchimiste et le marchand de Givrecime, puis revenir au Jarl.',
        pending: 'Ils sont tous sur la place, ou pas loin. Par ce temps, personne ne s’éloigne du feu.',
        complete: 'Voilà, tout le monde te connaît ! Ici, on ne laisse jamais geler quelqu’un dont on connaît le nom. Les autres… ça dépend de notre humeur.',
        objectives: [
            {
                kind: 'talk',
                label: 'Saluer le forgeron',
                npc: SMITH,
                line: 'De l’acier du sud ? Ça casse au premier gel, comme du verre ! Ici, je le trempe dans l’eau des glaciers, avec des éclats de givre. Mon grand-père forgeait déjà pour la famille du chef. Reviens quand tu voudras du solide.',
            },
            {
                kind: 'talk',
                label: 'Saluer le fondeur',
                npc: SMELTER,
                line: 'Tout ce que tu ne portes plus, je le refonds. Ici, rien ne pousse, alors on ne jette rien. Pas même les clous rouillés.',
            },
            {
                kind: 'talk',
                label: 'Saluer l’alchimiste',
                npc: ALCHEMIST,
                line: 'De la corne de yéti râpée dans du lait chaud : ça réchauffe jusqu’aux orteils ! Par contre, tu sens le chien mouillé pendant trois jours. Rapporte-moi ce que laissent les bêtes des montagnes, je t’en ferai des potions.',
            },
            {
                kind: 'talk',
                label: 'Saluer le marchand',
                npc: MERCHANT,
                line: 'Des habits en lin, ici ? Tu vas geler sur place et on te retrouvera comme une statue ! J’ai des armures de givre, faites pour nos hivers. Reviens me voir, je vais t’habiller correctement.',
            },
        ],
        reward: { xp: 350, gold: 70 },
    },
    {
        id: 'snow-wolves',
        name: 'Les loups de la passe',
        biome: 'snow',
        level: 16,
        requires: ['snow-council'],
        giver: JARL,
        turnIn: JARL,
        offer: 'Les loups des glaces viennent jusqu’aux murs du village ! Hier, ils ont volé deux chèvres, et ils ont même essayé d’attraper le petit de Brynja. Heureusement qu’il court vite ! Chasse-les et rapporte-moi leurs crocs, pour rassurer les mamans.',
        summary: 'Chasser les loups des glaces autour de Givrecime et rapporter leurs crocs au Jarl.',
        pending: 'Les loups chassent à quatre ou cinq. Ne les laisse pas t’encercler !',
        complete: 'Bravo ! Je vais poser ces crocs sur la table de la halle ce soir, ça va en impressionner plus d’un. Prends ces potions, l’alchimiste les fait spécialement pour nos nuits glacées. Elles ont un goût de chien mouillé, mais elles marchent.',
        objectives: [
            { kind: 'kill', label: 'Loups des glaces chassés', mobs: ['ice-wolf'], count: 25 },
            { kind: 'collect', label: 'Crocs de givre', item: 'ice-wolf-fang', count: 6 },
        ],
        reward: { xp: 900, gold: 180, items: [{ templateId: 'snow-tonic', quantity: 3 }] },
    },
    {
        id: 'snow-armor',
        name: 'Vêtu pour l’hiver',
        biome: 'snow',
        level: 16,
        requires: ['snow-council'],
        giver: MERCHANT,
        turnIn: MERCHANT,
        offer: 'Tu grelottes ! Tu claques même des dents. Il te faut un Casque de givre et un Plastron de givre. Achète-les et mets-les, je te rembourse une partie. Le chef t’aime bien, alors moi aussi.',
        summary: 'Acheter et porter le Casque de givre et le Plastron de givre.',
        pending: 'Achète le casque et le plastron, et mets-les tout de suite. Tu es tout bleu !',
        complete: 'Là, tu as l’air d’un vrai montagnard ! Enfin, de loin, dans le brouillard.',
        objectives: [
            { kind: 'wear', label: 'Porter un Casque de givre', items: anyRarity('frost-helm') },
            { kind: 'wear', label: 'Porter un Plastron de givre', items: anyRarity('frost-chest') },
        ],
        reward: { xp: 500, gold: 100 },
    },
    {
        id: 'snow-temper',
        name: 'Trempé dans la glace',
        biome: 'snow',
        level: 16,
        requires: ['snow-armor'],
        giver: SMITH,
        turnIn: SMITH,
        offer: 'Le marchand t’a vendu du bon acier, mais il n’est pas encore renforcé. Fais monter une pièce à quatre crans. Les éclats de givre, tu les trouveras sur les bêtes des montagnes, un par-ci, un par-là.',
        summary: 'Faire porter une pièce d’équipement à +4 par un forgeron.',
        pending: 'Il me faut une pièce à quatre crans. Garde des éclats d’avance, plus c’est haut, plus ça peut rater.',
        complete: 'Écoute comme ça sonne bien ! Tiens, prends ces éclats, je les ai trouvés dans la neige ce matin.',
        objectives: [{ kind: 'upgrade', label: 'Une pièce portée à +4', level: 4 }],
        reward: { xp: 600, gold: 120, items: [{ templateId: 'frost-shard', quantity: 4 }] },
    },
    {
        id: 'snow-saga',
        name: 'La saga du Géant',
        biome: 'snow',
        level: 16,
        requires: ['snow-wolves'],
        giver: JARL,
        turnIn: SKALD,
        offer: 'Tu veux savoir ce qui dort sur le Trône de Givre ? Runa, notre conteuse, te le chantera mieux que moi. Moi, je chante comme un ours. Elle est près de la grande halle. Apporte-lui deux pains : elle ne chante jamais le ventre vide. Et elle a toujours le ventre vide.',
        summary: 'Apporter deux pains à la Skalde Runa, près de la halle longue de Givrecime.',
        pending: 'Apporte deux pains à Runa. Le marchand en vend. Et ne les mange pas en chemin !',
        complete: 'Du pain ! Et il est encore chaud ! Assieds-toi, laisse-moi manger d’abord… Voilà. Je vais te chanter l’histoire du Géant de Givre, et de tous ceux qui ont essayé de le vaincre. Elle est longue. Il y a aussi un dernier couplet, que ma mère m’a appris sans jamais me l’expliquer. Celui-là, je ne le chante plus, il me donne des frissons.',
        objectives: [{ kind: 'collect', label: 'Pains', item: 'bread', count: 2 }],
        reward: { xp: 400, gold: 80 },
    },
    {
        id: 'snow-crystals',
        name: 'La meute des Cristaux',
        biome: 'snow',
        level: 17,
        requires: ['snow-saga'],
        giver: SKALD,
        turnIn: SEER,
        offer: 'Il y a vingt ans, Eirik, le frère du chef, est parti affronter le Géant avec douze guerriers. Aucun n’est revenu. Avant de partir, il était allé voir Eira, la voyante des Cristaux du Givre. Va la voir. Attention, les loups traînent autour des cristaux, ils adorent leur chant. Moi aussi, mais je ne mords pas.',
        summary: 'Rejoindre les Cristaux du Givre, chasser les loups qui les entourent et parler à la Voyante Eira.',
        pending: 'Les Cristaux du Givre sont au sud-est. Tu entendras les cristaux chanter avant de les voir, et les loups hurler avant les cristaux.',
        complete: 'C’est Runa qui t’envoie, je sais. Je t’ai vu arriver dans la glace, il y a trois nuits ! Il y avait du sang de loup sur tes mains, comme maintenant. Et derrière toi, une lumière rouge. Je ne sais pas encore ce qu’elle veut dire.',
        objectives: [
            { kind: 'reach', label: 'Atteindre les Cristaux du Givre', landmark: 'Cristaux du Givre' },
            { kind: 'kill', label: 'Loups des glaces chassés', mobs: ['ice-wolf'], count: 30 },
        ],
        reward: { xp: 1100, gold: 220 },
    },
    {
        id: 'snow-vision',
        name: 'Ce que voient les cristaux',
        biome: 'snow',
        level: 17,
        requires: ['snow-crystals'],
        giver: SEER,
        turnIn: SEER,
        offer: 'Mes cristaux peuvent montrer le passé, si on sait les faire chanter. Il me faut deux éclats de givre. Et demande à l’alchimiste quel sel fait chanter la glace. Il le sait très bien, il fait semblant de ne pas savoir. Reviens avec les deux, et je te montrerai Eirik.',
        summary: 'Rapporter deux éclats de givre à la Voyante Eira et interroger l’alchimiste de Givrecime.',
        pending: 'Il me faut deux éclats de givre et la réponse de l’alchimiste.',
        complete: 'Regarde dans le cristal. Non, plus près ! Là… Tu vois Eirik, blessé, qui donne sa hache à un jeune guerrier ? C’est Ulfar ! Il était tout jeune. Je crois qu’il a survécu. Et en dessous du Trône… non, la glace devient floue. Elle ne veut pas me montrer.',
        objectives: [
            { kind: 'collect', label: 'Éclats de givre', item: 'frost-shard', count: 2 },
            {
                kind: 'talk',
                label: 'Interroger l’alchimiste',
                npc: ALCHEMIST,
                line: 'Eira veut encore faire chanter la glace ? Dis-lui : trois pincées de sel de corne, et jamais pendant la pleine lune. Elle comprendra. Moi, en quarante ans, je n’ai jamais compris.',
            },
        ],
        reward: { xp: 900, gold: 180, items: [{ templateId: 'snow-tonic', quantity: 2 }] },
    },
    {
        id: 'snow-huntress',
        name: 'La Chasseuse de la Caverne',
        biome: 'snow',
        level: 17,
        requires: ['snow-vision'],
        giver: SEER,
        turnIn: HUNTRESS,
        offer: 'Pour aller au Trône, il faut passer par des chemins que seule Sigrun connaît. C’est la chasseuse d’ours, elle vit au nord-est, près de la Caverne de l’Ours. Elle n’aime personne. Dis-lui que c’est moi qui t’envoie. Elle va se moquer de moi, laisse-la faire.',
        summary: 'Rejoindre la Caverne de l’Ours, au nord-est, et trouver la Chasseuse Sigrun.',
        pending: 'La Caverne de l’Ours est au bout de la vallée, au nord-est. Suis les traces d’ours.',
        complete: 'C’est Eira qui t’envoie ? Elle et ses glaçons magiques… Bon. Tu es arrivé jusqu’ici sans te faire manger, et tu as encore tes dix doigts. Montre ? Hmm, neuf et demi. Ça ira.',
        objectives: [
            { kind: 'reach', label: 'Atteindre la Caverne de l’Ours', landmark: "Caverne de l'Ours" },
        ],
        reward: { xp: 450, gold: 90 },
    },
    {
        id: 'snow-bears',
        name: 'Les ours blancs',
        biome: 'snow',
        level: 17,
        requires: ['snow-huntress'],
        giver: HUNTRESS,
        turnIn: HUNTRESS,
        offer: 'Tu veux que je te guide ? Prouve-moi d’abord que tu sais chasser. Les ours blancs ont très faim cet hiver, ils attaquent tout ce qui bouge, même moi ! Rapporte-moi leurs fourrures. Le village en a besoin, et moi aussi, la mienne est trouée.',
        summary: 'Abattre les ours blancs des pics et rapporter leurs fourrures à la Chasseuse Sigrun.',
        pending: 'Un ours frappe fort, mais il tourne lentement. Ne reste jamais devant lui !',
        complete: 'Pas mal, pour quelqu’un du sud ! Prends ces potions. Et arrête de saigner sur ma neige, s’il te plaît.',
        objectives: [
            { kind: 'kill', label: 'Ours blancs abattus', mobs: ['polar-bear'], count: 30 },
            { kind: 'collect', label: 'Fourrures d’ours', item: 'bear-fur', count: 8 },
        ],
        reward: { xp: 1500, gold: 300, items: [{ templateId: 'snow-potion', quantity: 2 }] },
    },
    {
        id: 'snow-gear',
        name: 'Des mains qui ne gèlent pas',
        biome: 'snow',
        level: 17,
        requires: ['snow-bears'],
        giver: HUNTRESS,
        turnIn: HUNTRESS,
        offer: 'Tes doigts sont tout bleus ! Là-haut, si tu ne sens plus tes mains, tu lâches ton arme, et là, tu te fais manger. Va chez le marchand, achète des Gantelets et des Bottes de givre, et reviens.',
        summary: 'Acheter et porter les Gantelets de givre et les Bottes de givre.',
        pending: 'Achète les Gantelets et les Bottes de givre chez le marchand de Givrecime, et mets-les.',
        complete: 'C’est mieux ! Tiens, ce casque était à mon père. Il a perdu un combat contre un ours, mais le casque, lui, a tenu bon !',
        objectives: [
            { kind: 'wear', label: 'Porter des Gantelets de givre', items: anyRarity('frost-gloves') },
            { kind: 'wear', label: 'Porter des Bottes de givre', items: anyRarity('frost-boots') },
        ],
        reward: { xp: 700, gold: 140, items: [{ templateId: 'frost-helm-rare', quantity: 1 }] },
    },
    {
        id: 'snow-icebear',
        name: 'L’Ours des glaces',
        biome: 'snow',
        level: 18,
        requires: ['snow-gear'],
        giver: HUNTRESS,
        turnIn: HUNTRESS,
        offer: 'Il y a un ours que je chasse depuis six ans : l’Ours des glaces. Blanc comme la neige, avec des griffes comme des stalactites. C’est lui qui a battu mon père ! Il adore le miel givré des ruches de la vallée. Pose ce rayon de miel près d’une tanière d’ours blancs, il viendra. Ne le rate pas !',
        summary: 'Utiliser le rayon de miel givré près d’une tanière d’ours blancs, puis abattre l’Ours des glaces.',
        pending: 'Pose le miel près d’une tanière d’ours blancs, loin du village. S’il s’enfuit, recommence, il revient toujours pour le miel.',
        complete: 'Six ans que je le chasse… et c’est toi qui l’as eu ! Je devrais être jalouse. Bon, je le suis un peu. Prends ce plastron, je l’avais fait faire pour le jour où je l’aurais. Il te va mieux qu’à moi, de toute façon.',
        grants: [{ templateId: 'frost-honey', quantity: 1 }],
        objectives: [{ kind: 'kill', label: 'Ours des glaces abattu', mobs: ['ice-bear'], count: 1 }],
        reward: { xp: 1400, gold: 280, items: [{ templateId: 'frost-chest-rare', quantity: 1 }] },
    },
    {
        id: 'snow-camp',
        name: 'Le camp des neiges',
        biome: 'snow',
        level: 18,
        requires: ['snow-icebear'],
        giver: HUNTRESS,
        turnIn: JARL,
        offer: 'Dans les montagnes, des bêtes gardent des coffres. Ce sont les affaires des marchands que la tempête a emportés. Ouvre un de ces coffres. Ensuite, va voir le chef et dis-lui que je vais te guider. Il ne te croira pas. Insiste !',
        summary: 'Vaincre les gardiens d’un camp des pics, ouvrir son coffre, puis retrouver le Jarl.',
        pending: 'Bats les quatre gardiens, ensuite le coffre s’ouvrira.',
        complete: 'Sigrun va te guider ? Elle ne m’a jamais dit plus de trois mots, et je suis son chef depuis qu’elle est née ! Tu l’as impressionnée. Assieds-toi, je vais te parler de mon frère.',
        objectives: [{ kind: 'camp', label: 'Coffre d’un camp des pics', biome: 'snow', count: 1 }],
        reward: { xp: 1500, gold: 300, items: [{ templateId: 'frost-shard', quantity: 3 }] },
    },
    {
        id: 'snow-ridge',
        name: 'L’éclaireur de la Crête',
        biome: 'snow',
        level: 18,
        requires: ['snow-camp'],
        giver: JARL,
        turnIn: SCOUT,
        offer: 'Mon frère Eirik est parti par la Crête du Givre, tout au nord. Je l’ai vu partir depuis le mur du village, il m’a fait un signe de la main. Torvald, mon meilleur éclaireur, surveille là-haut depuis l’automne. S’il reste une trace de mon frère, il l’a vue. Va le voir.',
        summary: 'Rejoindre la Crête du Givre, au nord, et parler à l’Éclaireur Torvald.',
        pending: 'La Crête du Givre est tout au nord. Quand il n’y a plus rien après, tu y es.',
        complete: 'Quelqu’un ! Une vraie personne ! C’est le chef qui t’envoie ? Assieds-toi, j’ai de la viande séchée, et trois semaines de choses à raconter. Je n’ai vu personne depuis l’automne ! Enfin, personne de vivant.',
        objectives: [
            { kind: 'reach', label: 'Atteindre la Crête du Givre', landmark: 'Crête du Givre' },
        ],
        reward: { xp: 500, gold: 100 },
    },
    {
        id: 'snow-revenants',
        name: 'Les morts du col',
        biome: 'snow',
        level: 18,
        requires: ['snow-ridge'],
        giver: SCOUT,
        turnIn: SCOUT,
        offer: 'Justement, en parlant de ça : des fantômes de glace errent dans les montagnes, avec des boucliers de Givrecime. Ce sont les guerriers d’Eirik ! Le froid les a réveillés. Libère-les et rapporte-moi leurs cœurs gelés, qu’on puisse leur offrir un vrai repos. J’en connaissais deux.',
        summary: 'Libérer les revenants du givre et rapporter leurs cœurs gelés à l’Éclaireur Torvald.',
        pending: 'Les fantômes ne sentent rien et n’ont peur de rien. Frappe avant qu’ils t’entourent !',
        complete: 'Ils vont enfin pouvoir se reposer. Regarde ce bouclier : il y a un nom gravé dessus. Eirik ! Il faut prévenir le chef. Mais je ne veux pas être celui qui lui annonce.',
        objectives: [
            { kind: 'kill', label: 'Revenants du givre libérés', mobs: ['frost-revenant'], count: 30 },
            { kind: 'collect', label: 'Cœurs gelés', item: 'frozen-heart', count: 6 },
        ],
        reward: { xp: 1900, gold: 380, items: [{ templateId: 'snow-potion', quantity: 3 }] },
    },
    {
        id: 'snow-names',
        name: 'Le nom sur le bouclier',
        biome: 'snow',
        level: 18,
        requires: ['snow-revenants'],
        giver: SCOUT,
        turnIn: SKALD,
        offer: 'Tu peux le lui dire, toi ? Moi, je ne peux pas quitter mon poste. Et après, va voir Runa. Elle voudra ajouter ce nom à son histoire, pour qu’on ne l’oublie plus jamais.',
        summary: 'Porter la nouvelle au Jarl Haldor, puis à la Skalde Runa.',
        pending: 'Va d’abord voir le chef, puis Runa. Et vas-y doucement avec le chef.',
        complete: 'Eirik n’est donc pas tombé au Trône, mais sur le chemin du retour ! Et il avait donné sa hache à Ulfar pour qu’elle revienne au village. Ulfar n’est jamais revenu… mais personne ne l’a jamais retrouvé mort, non plus. Je vais changer mon couplet. Il me faudra trois nuits, et beaucoup de pain.',
        objectives: [
            {
                kind: 'talk',
                label: 'Prévenir le Jarl Haldor',
                npc: JARL,
                line: 'Le bouclier de mon frère… Vingt ans que j’attends des nouvelles, et maintenant, je ne sais pas quoi dire. Va voir Runa. Moi, j’ai besoin d’être un peu seul.',
            },
        ],
        reward: { xp: 700, gold: 140 },
    },
    {
        id: 'snow-reforge',
        name: 'Le fer des anciens',
        biome: 'snow',
        level: 18,
        requires: ['snow-names', 'snow-temper'],
        giver: SKALD,
        turnIn: SMITH,
        offer: 'D’après mon histoire, les guerriers d’Eirik avaient des armes trempées cinq fois dans le glacier. C’est dans le quatrième couplet, entre la chanson à boire et les adieux. Le forgeron sait faire ça, si tu lui apportes assez de givre. Fais monter une pièce à cinq crans.',
        summary: 'Faire porter une pièce d’équipement à +5 par un forgeron.',
        pending: 'Il me faut une pièce à cinq crans. Compte trois éclats de givre par essai. Et ne me regarde pas travailler, ça me porte malheur !',
        complete: 'Cinq trempes, comme mon grand-père faisait pour Eirik ! Prends ces cristaux de glace. Pour aller au-delà de cinq crans, les éclats ne suffiront plus.',
        objectives: [{ kind: 'upgrade', label: 'Une pièce portée à +5', level: 5 }],
        reward: { xp: 1000, gold: 200, items: [{ templateId: 'ice-crystal', quantity: 3 }] },
    },
    {
        id: 'snow-yetis',
        name: 'Les yétis de la Crête',
        biome: 'snow',
        level: 19,
        requires: ['snow-names'],
        giver: SCOUT,
        turnIn: SCOUT,
        offer: 'Les yétis bloquent le chemin vers le Trône ! Ils descendent des sommets depuis que le Géant s’agite, et ils s’assoient au milieu du passage, comme des gros tapis. Débarrasse-nous d’eux et rapporte-moi leurs cornes. On en fait des trompes de guerre. Et des cuillères.',
        summary: 'Chasser les yétis des pics et rapporter leurs cornes à l’Éclaireur Torvald.',
        pending: 'Un yéti, c’est comme trois ours à la fois. Bois tes potions avant d’être à terre, pas après !',
        complete: 'Le chemin est libre, pour l’instant. Mais la nuit, j’entends encore un hurlement, plus loin. Plus grave. J’ai compté : il hurle sept fois, toujours sept.',
        objectives: [
            { kind: 'kill', label: 'Yétis chassés', mobs: ['yeti'], count: 30 },
            { kind: 'collect', label: 'Cornes de yéti', item: 'yeti-horn', count: 6 },
        ],
        reward: { xp: 2200, gold: 440, items: [{ templateId: 'snow-potion', quantity: 2 }] },
    },
    {
        id: 'snow-howl',
        name: 'Le Yéti hurleur',
        biome: 'snow',
        level: 19,
        requires: ['snow-yetis'],
        giver: SCOUT,
        turnIn: SCOUT,
        offer: 'C’est le Yéti hurleur, le chef des yétis. Il ne vient que si on le provoque. J’ai fabriqué cette corne avec celles que tu m’as rapportées. Souffle dedans près d’une tanière de yétis, sept fois, comme lui. Il viendra.',
        summary: 'Sonner la corne des hurlements près d’une tanière de yétis, puis abattre le Yéti hurleur.',
        pending: 'Souffle dans la corne près d’une tanière de yétis, loin du village. S’il ne vient pas, recommence.',
        complete: 'Plus aucun hurlement ! Je vais enfin dormir. J’ai un peu oublié comment on fait, mais je vais essayer. Prends ces bottes, elles étaient à moi. J’en ai d’autres, presque aussi bien.',
        grants: [{ templateId: 'howl-horn', quantity: 1 }],
        objectives: [{ kind: 'kill', label: 'Yéti hurleur abattu', mobs: ['howling-yeti'], count: 1 }],
        reward: { xp: 1800, gold: 360, items: [{ templateId: 'frost-boots-epic', quantity: 1 }] },
    },
    {
        id: 'snow-survivor',
        name: 'Le survivant du Trône',
        biome: 'snow',
        level: 19,
        requires: ['snow-howl'],
        giver: SCOUT,
        turnIn: WARDEN,
        offer: 'Cette nuit, sans hurlements, j’ai pu regarder au lieu d’écouter. Et j’ai vu de la fumée près du Trône de Givre, au sud-est ! Un feu de camp. Quelqu’un vit là-bas, devant la forteresse. Si c’est Ulfar, il garde cette porte depuis vingt ans ! Va voir.',
        summary: 'Rejoindre le Trône de Givre, au sud-est de la Crête, et trouver celui qui y vit.',
        pending: 'Le Trône de Givre est au sud-est. Suis la fumée.',
        complete: 'Qui es-tu ? Personne ne monte jamais jusqu’ici. C’est Haldor qui t’envoie ? Il est toujours en vie, le petit frère ! Je suis Ulfar. Et cette hache, c’est celle d’Eirik. Je ne l’ai jamais lâchée.',
        objectives: [
            { kind: 'reach', label: 'Atteindre le Trône de Givre', landmark: 'Trône de Givre' },
        ],
        reward: { xp: 600, gold: 120 },
    },
    {
        id: 'snow-oath',
        name: 'Le serment d’Ulfar',
        biome: 'snow',
        level: 19,
        requires: ['snow-survivor'],
        giver: WARDEN,
        turnIn: WARDEN,
        offer: 'J’ai promis à Eirik de garder cette porte jusqu’à ce que quelqu’un termine ce qu’il avait commencé. Chaque nuit, le Géant appelle ses fantômes et ses bêtes. Affaiblis-le : bats ses fantômes et ses yétis, le plus possible.',
        summary: 'Abattre les revenants du givre et les yétis qui répondent à l’appel du Géant.',
        pending: 'Chaque bête que tu bats, c’est une de moins devant le Trône. Continue !',
        complete: 'Je les ai entendus tomber un par un, d’ici. Cette nuit, je vais peut-être dormir. Prends ces potions. Moi, je bois de la neige fondue, ça me suffit.',
        objectives: [
            { kind: 'kill', label: 'Revenants du givre abattus', mobs: ['frost-revenant'], count: 30 },
            { kind: 'kill', label: 'Yétis abattus', mobs: ['yeti'], count: 30 },
        ],
        reward: { xp: 2600, gold: 520, items: [{ templateId: 'snow-potion', quantity: 3 }] },
    },
    {
        id: 'snow-heart',
        name: 'Le cœur de l’hiver',
        biome: 'snow',
        level: 19,
        requires: ['snow-oath'],
        giver: WARDEN,
        turnIn: SEER,
        offer: 'Le froid du Géant peut te geler sur place avant même qu’il frappe. Eira sait fabriquer une protection contre ça, avec des cœurs gelés et des cornes de yéti. Rapporte-lui ce qu’il faut. Et dis-lui qu’Ulfar est vivant. Elle le sait sûrement, mais dis-le quand même.',
        summary: 'Rapporter des cœurs gelés et des cornes de yéti à la Voyante Eira, et abattre des revenants.',
        pending: 'Il me faut huit cœurs gelés et quatre cornes de yéti. Je commence dès que tu les as.',
        complete: 'Ulfar est vivant ! Je le savais, je le voyais dans la glace. Mais chaque nuit, la glace me montre aussi un œil rouge qui s’ouvre sous le Trône. J’espère que c’est celui du Géant… Voici ta protection : ce bouclier te gardera du froid.',
        objectives: [
            { kind: 'kill', label: 'Revenants du givre abattus', mobs: ['frost-revenant'], count: 20 },
            { kind: 'collect', label: 'Cœurs gelés', item: 'frozen-heart', count: 8 },
            { kind: 'collect', label: 'Cornes de yéti', item: 'yeti-horn', count: 4 },
        ],
        reward: { xp: 1800, gold: 360, items: [{ templateId: 'frost-shield-epic', quantity: 1 }] },
    },
    {
        id: 'snow-edge',
        name: 'Le tranchant du Nord',
        biome: 'snow',
        level: 19,
        requires: ['snow-heart', 'snow-reforge'],
        giver: SMITH,
        turnIn: SMITH,
        offer: 'Eira m’a dit que tu allais au Trône. Alors cinq crans, ça ne suffit plus, il en faut six ! Il faut un cristal de glace par essai, et le marteau rate plus souvent qu’il ne réussit. Personne de Givrecime n’y est jamais allé aussi bien armé. Même pas Eirik !',
        summary: 'Faire porter une pièce d’équipement à +6 par un forgeron.',
        pending: 'Il me faut une pièce à six crans. Les cristaux de glace, tu les trouveras sur les gardiens des camps.',
        complete: 'Six crans ! Même mon grand-père n’y est jamais arrivé. Je vais aller le lui raconter sur sa tombe, il va être vert de jalousie. Prends ce cœur de glacier, il ne sert que pour les plus grands travaux.',
        objectives: [{ kind: 'upgrade', label: 'Une pièce portée à +6', level: 6 }],
        reward: { xp: 1500, gold: 300, items: [{ templateId: 'glacier-heart', quantity: 1 }] },
    },
    {
        id: 'snow-blizzard',
        name: 'Le Vieux Blizzard',
        biome: 'snow',
        level: 20,
        requires: ['snow-heart'],
        giver: WARDEN,
        turnIn: WARDEN,
        offer: 'Il reste un dernier gardien : le Vieux Blizzard, un yéti si vieux que la tempête le suit partout. C’est lui qui nous a battus, il y a vingt ans. Cet orbe contient un morceau de sa tempête. Casse-le près d’une tanière de yétis, il viendra le récupérer. Quand il tombera, le vent tombera avec lui.',
        summary: 'Briser l’orbe de tempête près d’une tanière de yétis et abattre le Vieux Blizzard.',
        pending: 'Casse l’orbe près d’une tanière de yétis. Et emporte des potions, il est très fort.',
        complete: 'Le vent s’est arrêté ! Pour la première fois depuis vingt ans, je vois le Trône sans tempête. Il est plus petit que dans mes souvenirs. Prends ce casque, tu es prêt.',
        grants: [{ templateId: 'storm-orb', quantity: 1 }],
        objectives: [
            { kind: 'kill', label: 'Vieux Blizzard abattu', mobs: ['elder-blizzard'], count: 1 },
        ],
        reward: { xp: 2500, gold: 500, items: [{ templateId: 'frost-helm-epic', quantity: 1 }] },
    },
    {
        id: 'snow-muster',
        name: 'Le ban de Givrecime',
        biome: 'snow',
        level: 20,
        requires: ['snow-blizzard'],
        giver: WARDEN,
        turnIn: JARL,
        offer: 'Va dire à Haldor que le chemin est libre. Mais n’arrive pas les mains vides : ouvre un dernier coffre en route, et chasse les ours qui rôdent autour du village. Et dis-lui que je garde la hache, il comprendra.',
        summary: 'Ouvrir le coffre d’un camp des pics, abattre les ours blancs, puis prévenir le Jarl Haldor.',
        pending: 'Ouvre un coffre, chasse les ours, puis va voir Haldor à la halle.',
        complete: 'Ulfar est vivant ? Et il garde la hache, évidemment, ça ne m’étonne pas de lui ! Et le Vieux Blizzard est tombé ? Alors c’est le moment ! Que tous ceux qui peuvent porter une arme viennent à la halle ce soir !',
        objectives: [
            { kind: 'camp', label: 'Coffre d’un camp des pics', biome: 'snow', count: 1 },
            { kind: 'kill', label: 'Ours blancs abattus', mobs: ['polar-bear'], count: 30 },
        ],
        reward: { xp: 2400, gold: 480, items: [{ templateId: 'snow-potion', quantity: 3 }] },
    },
    {
        id: 'snow-giant',
        name: 'Le Trône de Givre',
        biome: 'snow',
        level: 20,
        requires: ['snow-muster'],
        giver: JARL,
        turnIn: JARL,
        offer: 'Mon frère est parti avec douze guerriers. Toi, emmène qui tu veux. Le Géant de Givre t’attend au fond de sa forteresse, assis sur son trône. Va le battre. Et surtout, reviens ! Je ne veux pas d’une deuxième histoire triste.',
        summary: 'Abattre le Géant de Givre au fond du Trône de Givre, puis revenir au Jarl.',
        pending: 'Le Trône de Givre est à l’est. Emporte des potions, et reviens-nous entier !',
        complete: 'Le Géant de Givre est tombé ?! Écoute… le vent s’est arrêté sur toute la vallée ! Tu as fait ce que mon frère n’a pas pu faire. Par contre… tu sens ? Ça gronde encore, sous la halle. Sûrement la glace qui se tasse. Sûrement. Prends ce plastron, on l’a forgé pour le jour où quelqu’un reviendrait du Trône.',
        objectives: [{ kind: 'kill', label: 'Géant de Givre abattu', mobs: ['frost-giant'], count: 1 }],
        reward: { xp: 3000, gold: 600, items: [{ templateId: 'frost-chest-epic', quantity: 1 }] },
    },
    {
        id: 'snow-farewell',
        name: 'La fin de l’hiver',
        biome: 'snow',
        level: 20,
        requires: ['snow-giant'],
        giver: JARL,
        turnIn: SKALD,
        offer: 'Avant que Runa chante ton histoire, va voir ceux qui t’ont aidé. Ulfar d’abord, sa promesse est tenue. Puis Sigrun, Torvald et Eira. Il faut qu’ils l’apprennent par toi.',
        summary: 'Porter la nouvelle à Ulfar, Sigrun, Torvald et Eira, puis revenir à la Skalde Runa.',
        pending: 'Ils sont tous à leur poste, aux quatre coins des montagnes. Ils t’attendent.',
        complete: 'Ils savent tous ? Alors écoute le dernier couplet, celui que je ne chante plus : « Que le froid siège au-dessus du feu, et le feu dormira. » J’ai toujours cru que le froid, c’était l’hiver. Mais si c’était le Géant, assis sur son trône ? Au-dessus de… Oh non. Qu’est-ce qu’on a fait ?',
        objectives: [
            {
                kind: 'talk',
                label: 'Délier Ulfar de son serment',
                npc: WARDEN,
                line: 'Ma promesse est tenue, merci. Mais quand le Géant est tombé, le sol a bougé sous mes pieds, comme une porte qu’on déverrouille. Je ne redescends pas tout de suite. Quelque chose ne va pas sous cette glace.',
            },
            {
                kind: 'talk',
                label: 'Prévenir la Chasseuse Sigrun',
                npc: HUNTRESS,
                line: 'Le Géant ? Je savais que tu y arriverais, depuis l’Ours des glaces. Mais ce matin, les ours dévalaient la montagne en courant, les oreilles couchées. Un ours qui fuit, je n’avais jamais vu ça.',
            },
            {
                kind: 'talk',
                label: 'Prévenir l’Éclaireur Torvald',
                npc: SCOUT,
                line: 'Je l’ai vu d’ici ! La tempête s’est ouverte au-dessus du Trône, d’un coup. Et en dessous, pendant un instant, la glace est devenue rouge, comme une braise. Je boirai à ta santé ce soir. Deux fois : la deuxième, pour oublier ce que j’ai vu.',
            },
            {
                kind: 'talk',
                label: 'Prévenir la Voyante Eira',
                npc: SEER,
                line: 'La glace ne me montre plus l’hiver. Elle me montre de l’eau qui coule sous la neige, et une chaîne cassée. Ce matin, j’ai cru voir le printemps. Mais je ne crois pas que ce soit le printemps.',
            },
        ],
        reward: { xp: 800, gold: 160, items: [{ templateId: 'snow-potion', quantity: 2 }] },
    },
    {
        id: 'snow-homecoming',
        name: 'Le retour du héros',
        biome: 'snow',
        level: 20,
        requires: ['snow-breach'],
        giver: SKALD,
        turnIn: CAPTAIN,
        offer: 'Ton histoire attendra, je ne sais pas encore comment elle finit. Retourne à Havrebourg voir le capitaine Aldric. Raconte-lui le Géant, et ce qui s’est réveillé en dessous. Qu’ils se préparent, au sud. Le chef lui a envoyé un cadeau pour toi.',
        summary: 'Retourner à Havrebourg et raconter au Capitaine Aldric la chute du Géant et l’éveil de Shefira.',
        pending: 'Havrebourg est loin au sud. Prends l’arche, ça ira bien plus vite.',
        complete: 'Le Géant de Givre est tombé… et un dragon est libre ?! Givrecoeur le répétait depuis trois ans, et moi, je l’ai mis à la porte. Quel idiot je fais. Le chef de Givrecime m’a envoyé ceci : l’épée de son frère Eirik. Il veut qu’elle parte avec toi. Va voir Givrecoeur. Et dis-lui que la garde de Havrebourg est avec lui. Et que je suis désolé.',
        objectives: [{ kind: 'reach', label: 'Retourner à Havrebourg', landmark: 'Havrebourg' }],
        reward: {
            xp: 500,
            gold: 100,
            items: [{ templateId: 'eirik-sword', quantity: 1 }],
        },
    },
];
//# sourceMappingURL=snow-quests.js.map